import { Injectable } from '@angular/core';
import {
	HttpRequest,
	HttpHandler,
	HttpEvent,
	HttpInterceptor,
	HttpResponse,
	HttpErrorResponse,
	HttpStatusCode
} from '@angular/common/http';
import { catchError, filter, map, Observable } from 'rxjs';
import { TransferBaseResponseDto } from '../external-transfer/dtos';
import { TranslateService } from '@ngx-translate/core';
import { LoggingService } from '@shared/logging';
import { ApiErrorModel } from './models/api-error.model';

@Injectable({
	providedIn: 'root'
})
export class ErrorHandlerIntercetor implements HttpInterceptor {
	public constructor(
		private translateService: TranslateService,
		private loggingService: LoggingService
	) {}

	public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		return next.handle(request).pipe(
			filter((response) => response instanceof HttpResponse),
			map((response: HttpResponse<TransferBaseResponseDto>) => {
				const apiError: ApiErrorModel = {
					apiErrorCode: -1,
					httpStatusCode: response.status,
					errorMessage: ''
				};

				this.handleErrorsArray(response, apiError);
				this.handleErrorsCodeAndMessage(response, apiError);

				return response;
			}),
			catchError((error: HttpErrorResponse | ApiErrorModel) => {
				if (error instanceof HttpErrorResponse) {
					this.turnHttpErrorIntoApiErrorModel(error);
				}

				throw error;
			})
		);
	}

	private turnHttpErrorIntoApiErrorModel(error: HttpErrorResponse): ApiErrorModel {
		let errorMessage: string = error.message;
		let errorStatus: number = error.status;

		// todo: use a dedicated plugin for checking internet connection
		if (typeof error.status !== 'undefined') {
			if (!error.status) {
				errorMessage = this.translateService.instant('api.errorMessages.noInternetConnection');
				errorStatus = HttpStatusCode.ServiceUnavailable;
			} else {
				errorMessage = this.translateService.instant('api.errorMessages.noServerAvailability');
			}
		}

		const apiError: ApiErrorModel = {
			apiErrorCode: -1,
			httpStatusCode: errorStatus,
			errorMessage
		};

		throw apiError;
	}

	private handleErrorsArray(
		response: HttpResponse<TransferBaseResponseDto>,
		apiError: ApiErrorModel
	  ) {
		const errors = response?.body?.Errors;
	  
		if (Array.isArray(errors)) {
			if (errors.length > 0) {
				const joinedErrors = errors.join(' | ');
				this.loggingService.error('2xx success but error array', joinedErrors);
			
				apiError = {
				  ...apiError,
				  errorMessage: joinedErrors
				};
				throw apiError;
			  }
		} else if (errors) {
			// Handle non-array error formats (string, object, etc.)
			const fallbackMessage =
			  typeof errors === 'string'
				? errors
				: JSON.stringify(errors);
		
			this.loggingService.error('2xx success but unexpected error format', fallbackMessage);
		
			apiError = {
			  ...apiError,
			  errorMessage: fallbackMessage
			};
			throw apiError;
		  }
	  }

	private handleErrorsCodeAndMessage(response: HttpResponse<TransferBaseResponseDto>, apiError: ApiErrorModel) {
		if (response.body && response.body.ErrorCode && response.body.ErrorCode > 0) {
			const errorCode = response.body.ErrorCode && response.body.ErrorCode > 0 ? response.body.ErrorCode : -1;
			const errorMessage =
				response.body.ErrorMessage && response.body.ErrorMessage !== ''
					? response.body.ErrorMessage
					: 'Unknown error';

			this.loggingService.error(
				'2xx success but error code',
				`Error code: ${errorCode} with error message: ${errorMessage}`
			);

			this.loggingService.error('2xx success but errorMessage', errorMessage);

			apiError = {
				...apiError,
				apiErrorCode: errorCode,
				errorMessage: `Something went wrong (error ${errorCode} - ${errorMessage}). Please export the logs to retrieve the error.`
			};

			throw apiError;
		}
	}
}
